import React from 'react';

interface ProBadgeProps {
  tier: 'free' | 'pro' | 'premium' | 'business';
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

/**
 * Pro 멤버십 뱃지 컴포넌트
 * 사용자의 구독 등급에 따라 뱃지 표시
 */
const ProBadge: React.FC<ProBadgeProps> = ({ 
  tier, 
  size = 'medium',
  showLabel = true 
}) => {
  // Free 등급은 뱃지 표시하지 않음
  if (tier === 'free') return null;

  const getBadgeConfig = () => {
    switch (tier) {
      case 'pro':
        return {
          icon: '⭐',
          label: 'PRO',
          bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderColor: '#667eea',
          textColor: '#fff'
        };
      case 'premium':
        return {
          icon: '💎',
          label: 'PREMIUM',
          bgColor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          borderColor: '#f093fb',
          textColor: '#fff'
        };
      case 'business':
        return {
          icon: '🏢',
          label: 'BUSINESS',
          bgColor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          borderColor: '#4facfe',
          textColor: '#fff'
        };
      default:
        return null;
    }
  };

  const config = getBadgeConfig();
  if (!config) return null;

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          padding: '2px 6px',
          fontSize: '10px',
          iconSize: '10px',
          borderRadius: '4px'
        };
      case 'large':
        return {
          padding: '6px 14px',
          fontSize: '14px',
          iconSize: '16px',
          borderRadius: '8px'
        };
      default: // medium
        return {
          padding: '4px 10px',
          fontSize: '12px',
          iconSize: '12px',
          borderRadius: '6px'
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: sizeStyles.padding,
        background: config.bgColor,
        color: config.textColor,
        fontSize: sizeStyles.fontSize,
        fontWeight: 'bold',
        borderRadius: sizeStyles.borderRadius,
        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        whiteSpace: 'nowrap'
      }}
    >
      <span style={{ fontSize: sizeStyles.iconSize }}>{config.icon}</span>
      {showLabel && <span>{config.label}</span>}
    </span>
  );
};

/**
 * 사용자 이름 옆에 Pro 뱃지를 함께 표시하는 컴포넌트
 */
interface UserNameWithBadgeProps {
  name: string;
  tier: 'free' | 'pro' | 'premium' | 'business';
  size?: 'small' | 'medium' | 'large';
}

export const UserNameWithBadge: React.FC<UserNameWithBadgeProps> = ({
  name,
  tier,
  size = 'medium'
}) => {
  return (
    <span style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '8px' 
    }}>
      <span>{name}</span>
      <ProBadge tier={tier} size={size} />
    </span>
  );
};

/**
 * 카드/리스트 아이템용 작은 Pro 뱃지
 */
interface ProBadgeSmallProps {
  isPro: boolean;
}

export const ProBadgeSmall: React.FC<ProBadgeSmallProps> = ({ isPro }) => {
  if (!isPro) return null;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 6px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        fontSize: '10px',
        fontWeight: 'bold',
        borderRadius: '4px',
        marginLeft: '6px'
      }}
    >
      ⭐ PRO
    </span>
  );
};

/**
 * 프리랜서 카드용 Pro 뱃지 (우측 상단 위치)
 */
interface ProBadgeCornerProps {
  tier: 'free' | 'pro' | 'premium' | 'business';
}

export const ProBadgeCorner: React.FC<ProBadgeCornerProps> = ({ tier }) => {
  if (tier === 'free') return null;

  const config = {
    pro: { icon: '⭐', label: 'PRO', bg: '#667eea' },
    premium: { icon: '💎', label: 'PREMIUM', bg: '#f093fb' },
    business: { icon: '🏢', label: 'BIZ', bg: '#4facfe' }
  }[tier];

  if (!config) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        backgroundColor: config.bg,
        color: '#fff',
        fontSize: '11px',
        fontWeight: 'bold',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        zIndex: 1
      }}
    >
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </div>
  );
};

export default ProBadge;
